import { useState } from "react";
import { Badge, Button, Card, Chip, Group, Skeleton, Stack, Text } from "@mantine/core";
import { IconArrowBackUp, IconCheck } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { passio } from "../../lib/clients";
import { composeMessage } from "../../lib/clientSettings";
import { useSession } from "../../lib/session";
import { SAMPLE_PHOTOS, sampleDataUri } from "../../lib/samples";
import { ErrorNotice } from "../../components/common";
import { FoodRow, MealGroupsView } from "../../components/food";
import { ImagePicker } from "../../components/ImagePicker";
import { nameOf, PickList, RunBar, Step, togglePick, TotalsCompare, TourTimeline, type TourFood } from "./shared";

const LOOKALIKE_CONTEXT = "Recognized in a photo; suggest foods that look similar and could be mistaken for it.";

/**
 * Camera flow: recognize the photo, offer look-alikes for an item the camera may have got wrong
 * (coffee vs tea, cucumber vs zucchini), then ask for what a photo can't show (sugar, milk, dressing).
 */
export function PhotoTour({ onSelect }: { onSelect: (f: TourFood) => void }) {
  const { hasFeature } = useSession();
  const [image, setImage] = useState<string | null>(null);
  const [focus, setFocus] = useState(0);
  const [replaced, setReplaced] = useState<Record<number, TourFood>>({});
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const extract = useMutation({ mutationFn: (img: string) => passio.vision.extractIngredientsAutoTyped({ image: img, message: composeMessage("") }) });
  const alt = useMutation({ mutationFn: (food: string) => passio.tools.visualAlternativeSuggestions(food, LOOKALIKE_CONTEXT) });
  const hidden = useMutation({ mutationFn: (foods: string) => passio.tools.invisibleIngredientSuggestions(foods) });
  const [running, setRunning] = useState(false);

  const can = { vision: hasFeature("vision"), text: hasFeature("text_ai") };
  const scanned = extract.data?.data ?? [];
  const current: TourFood[] = scanned.map((f, i) => replaced[i] ?? f);
  const extras = hidden.data?.data ?? [];
  const chosen = extras.filter((_, i) => picked.has(i));
  const focusName = scanned[focus] ? nameOf(scanned[focus]) : "";

  const reset = () => {
    [extract, alt, hidden].forEach((m) => m.reset());
    setFocus(0);
    setReplaced({});
    setPicked(new Set());
  };

  const recheckHidden = (foods: TourFood[]) => {
    setPicked(new Set());
    hidden.mutate(foods.map(nameOf).join(", "));
  };

  const runAll = async () => {
    reset();
    setRunning(true);
    try {
      const img = image ?? (await sampleDataUri(SAMPLE_PHOTOS[0].src));
      if (!image) setImage(img);
      const found = (await extract.mutateAsync(img)).data;
      if (found.length && can.text) {
        await Promise.allSettled([alt.mutateAsync(nameOf(found[0])), hidden.mutateAsync(found.map(nameOf).join(", "))]);
      }
    } catch {
      // Each step shows its own error.
    } finally {
      setRunning(false);
    }
  };

  const checkItem = (i: number) => {
    setFocus(i);
    alt.mutate(nameOf(scanned[i]));
  };

  const swapIn = (f: TourFood) => {
    const next = { ...replaced, [focus]: f };
    setReplaced(next);
    recheckHidden(scanned.map((s, i) => next[i] ?? s));
  };

  const undo = (i: number) => {
    const next = { ...replaced };
    delete next[i];
    setReplaced(next);
    recheckHidden(scanned.map((s, j) => next[j] ?? s));
  };

  const busy = running || extract.isPending;
  const active = hidden.data && extract.data ? 4 : alt.data ? 2 : extract.data ? 1 : 0;

  if (!can.vision) {
    return (
      <Card>
        <Text c="dimmed">Photo recognition isn't enabled for this account, so the photo tour can't run.</Text>
      </Card>
    );
  }

  return (
    <Stack gap="lg">
      <Card p={{ base: "md", sm: "xl" }}>
        <Stack gap="sm">
          <ImagePicker value={image} onChange={(v) => setImage(v)} label="Drop a meal or drink photo, or pick a sample below" samples={SAMPLE_PHOTOS} />
          <RunBar
            busy={busy}
            canRun
            hasResults={active > 0}
            onRun={runAll}
            onClear={reset}
            runLabel={image ? "Run the tour" : "Run with the coffee photo"}
            hint="Try the black coffee: the camera sees a dark drink, so step 2 offers tea or cola, and step 3 asks about the sugar and milk it can't see."
          />
        </Stack>
      </Card>

      <TourTimeline
        active={active}
        steps={[
          {
            label: "Recognize the photo",
            body: (
              <Step title="Photo to foods (auto-typed)" route="POST /napi/tools/vision/extractIngredientsAutoTyped" to="/photo" done={Boolean(extract.data)}>
                {extract.error ? <ErrorNotice error={extract.error} /> : null}
                {extract.isPending && <Skeleton h={120} radius="md" />}
                {extract.data && !extract.isPending && <MealGroupsView items={scanned} onSelect={onSelect} fallbackMealName="Your photo" emptyText="Nothing recognized. Try another photo." />}
                {!extract.data && !extract.isPending && !extract.error && (
                  <Text fz="sm" c="dimmed">
                    The auto-typed route works out what kind of photo it is (plate, drink, package, barcode) and returns matched foods with portions.
                  </Text>
                )}
              </Step>
            ),
          },
          {
            label: "Did you mean…?",
            body: (
              <Step title={focusName && alt.data ? `Looks like ${focusName}` : "Look-alikes for a doubtful item"} route="POST /napi/tools/visualAlternativeSuggestions" to="/alternatives" done={Boolean(alt.data)} locked={can.text ? undefined : "Text AI tools aren't enabled for this account."}>
                {scanned.length > 1 && (
                  <Group gap={6} mb="sm">
                    <Text fz="xs" c="dimmed">
                      Check another item:
                    </Text>
                    {scanned.map((f, i) => (
                      <Chip key={i} size="xs" checked={i === focus} onChange={() => checkItem(i)} disabled={alt.isPending}>
                        {nameOf(f)}
                      </Chip>
                    ))}
                  </Group>
                )}
                {alt.error ? <ErrorNotice error={alt.error} /> : null}
                {alt.isPending && <Skeleton h={120} radius="md" />}
                {alt.data && !alt.isPending && (
                  <Stack gap={2}>
                    <Text fz="sm" c="dimmed" mb={4}>
                      Photos can be ambiguous. Offer these when the user says the scan got it wrong; picking one swaps it into the log and re-runs step 3.
                    </Text>
                    {alt.data.data.slice(0, 6).map((it, i) => {
                      const inUse = replaced[focus] === it;
                      return (
                        <FoodRow
                          key={i}
                          item={it}
                          onClick={it.refCode ? () => onSelect(it) : undefined}
                          right={
                            inUse ? (
                              <Badge color="lime.8" leftSection={<IconCheck size={12} />} mt={6}>
                                In the log
                              </Badge>
                            ) : (
                              <Button
                                size="compact-xs"
                                variant="light"
                                mt={6}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  swapIn(it);
                                }}
                              >
                                Use instead
                              </Button>
                            )
                          }
                        />
                      );
                    })}
                  </Stack>
                )}
                {!alt.data && !alt.isPending && !alt.error && (
                  <Text fz="sm" c="dimmed">
                    A dark drink could be coffee, tea or cola; green slices could be cucumber or zucchini. This returns foods that look like the recognized one, each matched with nutrition, for a one-tap correction.
                  </Text>
                )}
              </Step>
            ),
          },
          {
            label: "What the camera can't see",
            body: (
              <Step title="Hidden ingredients" route="POST /napi/tools/invisibleIngredientSuggestions" to="/dish" done={Boolean(hidden.data)} locked={can.text ? undefined : "Text AI tools aren't enabled for this account."}>
                {hidden.error ? <ErrorNotice error={hidden.error} /> : null}
                {hidden.isPending && <Skeleton h={100} radius="md" />}
                {hidden.data && !hidden.isPending && (
                  <Stack gap="sm">
                    <Text fz="sm" c="dimmed">
                      Asked about “{hidden.variables}”. Sugar in a coffee or dressing on a salad doesn't show in a photo; tick what the user confirms.
                    </Text>
                    {extras.length ? <PickList items={extras} picked={picked} onToggle={(i) => setPicked((s) => togglePick(s, i))} onOpen={onSelect} /> : <Text c="dimmed">Nothing hidden suggested.</Text>}
                  </Stack>
                )}
                {!hidden.data && !hidden.isPending && !hidden.error && (
                  <Text fz="sm" c="dimmed">
                    Runs on the recognized foods (after any correction in step 2) and suggests what's usually added but invisible: sugar, milk, oil, dressing.
                  </Text>
                )}
              </Step>
            ),
          },
          {
            label: "The log entry",
            body: (
              <Step title="What gets logged" route="No call: totals from each food's nutritionPreview" to="/nutrition-math" done={Boolean(extract.data && hidden.data)}>
                {extract.data ? (
                  <Stack gap="sm">
                    <Stack gap={2}>
                      {current.map((f, i) => (
                        <FoodRow
                          key={`f-${i}`}
                          item={f}
                          onClick={f.refCode ? () => onSelect(f) : undefined}
                          right={
                            replaced[i] ? (
                              <Button size="compact-xs" variant="subtle" color="gray" mt={6} leftSection={<IconArrowBackUp size={12} />} onClick={(e) => {
                                  e.stopPropagation();
                                  undo(i);
                                }}>
                                Was {nameOf(scanned[i])}
                              </Button>
                            ) : undefined
                          }
                        />
                      ))}
                      {chosen.map((f, i) => (
                        <FoodRow key={`x-${i}`} item={f} right={<Badge variant="light" color="violet" mt={6}>Added</Badge>} />
                      ))}
                    </Stack>
                    <TotalsCompare before={scanned} after={[...current, ...chosen]} beforeLabel="As scanned" afterLabel="Corrected + extras" />
                  </Stack>
                ) : (
                  <Text fz="sm" c="dimmed">
                    The corrected foods plus confirmed extras, with totals next to what the camera alone saw. The Nutrition math page shows how to compute these yourself.
                  </Text>
                )}
              </Step>
            ),
          },
        ]}
      />
    </Stack>
  );
}
